import { COMMON_TEXT } from '@/helpers/common-text';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import { FieldArray, Form, Formik, getIn } from 'formik';
import { cloneDeep } from 'lodash';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import LoadingSpinner from 'src/components/CustomComponent/LoadingSpinner';
import Toaster from 'src/components/CustomComponent/Toaster';
import SaveButton from 'src/components/CustomComponent/SaveButton/SaveButton';
import { getConfigurationUrlWithParam } from 'src/helpers/utility';
import * as Yup from 'yup';
import { fetchOrganizationList } from './query-request';
import { createAuthenticatedApiClient } from 'src/helpers/api-client';
import { WorkflowsApi } from '@/openapi/apis/WorkflowsApi';

Yup.addMethod(Yup.array, 'orgIdentiferUnique', function (field, message) {
  return this.test('orgIdentiferUnique', message, function (array) {
    const uniqueData = Array.from(
      new Set(array.map(row => row[field]?.toLowerCase()))
    );
    const isUnique = array.length === uniqueData.length;
    if (isUnique) {
      return true;
    }
    const index = array.findIndex(
      (row, i) => row[field]?.toLowerCase() !== uniqueData[i]
    );
    return this.createError({
      path: `${this.path}.${index}.${field}`,
      message,
    });
  });
});

@connect(state => ({
  tenantId: state.querystring?.['select-tenant']?.[0],
  querystring: state.querystring,
  sessionStore: state.session,
}))
class OrganizationSetting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenant:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0],
      isLoading: false,
      orgList: [],
    };
    this.originalOrgList = [];
    this.mainRowContainerRef = React.createRef();
    this.orgProductTypeOptions = [
      { label: 'Meraki', value: 'meraki' },
      { label: 'Verkada', value: 'verkada' },
    ];
  }

  componentDidMount() {
    const { tenant } = this.state;
    if (tenant) {
      this.setState({ tenant }, () => {
        this.getData();
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { querystring } = this.props;
    const { querystring: prevQuerystring } = prevProps;
    const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const prevTenant =
      prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const isTenantChanged = tenant !== prevTenant;
    if (isTenantChanged) {
      this.setState({ tenant }, () => {
        this.getData();
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return true;
  }

  goBackToConfigPage = () => {
    const { querystring } = this.props;
    window.location.href = getConfigurationUrlWithParam({ querystring });
  };

  getData = async () => {
    const { tenant } = this.state;
    this.setState({ isLoading: true });
    let organizationList = await fetchOrganizationList({ tenantId: tenant });
    organizationList = organizationList || [];
    const orgs = organizationList.map(item => {
      return {
        orgId: item.org_id,
        orgIdentifer: item.org_identifer,
        orgName: item.org_name,
        product: item.product,
        tenantId: item.tenant_id,
      };
    });
    this.originalOrgList = cloneDeep(orgs);
    this.setState({
      orgList: cloneDeep(orgs),
      isLoading: false,
    });
  };

  findItemChanges({ updatedArray, originalArray }) {
    // 1. Identify new items
    const newItems = updatedArray.filter(item => item.orgId === null);

    // 2. Identify deleted items
    // item is deleted if it's in originalArray but not in updatedArray
    let deletedItems = originalArray.filter(
      item =>
        !updatedArray.find(itemA => itemA.orgId && itemA.orgId === item.orgId)
    );
    for (let item of deletedItems) {
      item.orgIdentifer = null;
    }

    // 3. Identify updated items, updated items should not be in newItems or deletedItems
    const updatedItems = updatedArray.filter(itemA => {
      const originalItem = originalArray.find(
        itemO => itemO.orgId === itemA.orgId
      );

      if (deletedItems.find(itemD => itemD.orgId === itemA.orgId)) {
        return false;
      }

      if (!originalItem) {
        return false; // This means it's a new item or something went wrong
      }

      // Check if any attribute is different
      return (
        itemA.orgName !== originalItem.orgName ||
        itemA.orgIdentifer !== originalItem.orgIdentifer ||
        itemA.product !== originalItem.product
      );
    });

    return {
      newItems: newItems,
      deletedItems: deletedItems,
      updatedItems: updatedItems,
    };
  }

  getUpdateDataPayload = ({ newItems, deletedItems, updatedItems }) => {
    let payload = {
      values: [],
    };
    for (let item of newItems) {
      payload.values.push({
        id: null,
        identifer: item.orgIdentifer,
        name: item.orgName,
        product: item.product,
      });
    }

    for (let item of deletedItems) {
      payload.values.push({
        id: item.orgId,
        identifer: null,
      });
    }

    for (let item of updatedItems) {
      payload.values.push({
        id: item.orgId,
        identifer: item.orgIdentifer,
        name: item.orgName,
        product: item.product,
      });
    }

    return payload;
  };

  updateOrganizationInfo = async ({ updatePayLoad }) => {
    try {
      const workflowsApi = createAuthenticatedApiClient(WorkflowsApi);
      const updateRes = await workflowsApi.updateSettingsOrganizations({
        apiPostWorkflowRequest: {
          ...updatePayLoad,
          tenantId: this.props.tenantId,
        }
      });

      const { success } = updateRes;
      if (!success) {
        throw Error('UPDATE SETTING ERROR');
      }
      return { isSuccess: true };
    } catch (error) {
      console.error('UPDATE SETTING ERROR', error);
    }
    return { isSuccess: false };
  };

  updateData = async ({ updatePayLoad }) => {
    this.setState({ isUpdating: true });
    const { isSuccess } = await this.updateOrganizationInfo({
      updatePayLoad,
    });

    if (isSuccess) {
      this.setState({ isUpdating: false }, () => {
        Toaster.success(COMMON_TEXT.SAVED_SUCCESSFULLY);
        this.getData();
      });
    } else {
      this.setState({ isUpdating: false }, () => {
        Toaster.error(COMMON_TEXT.SAVE_FAILED);
      });
    }
  };

  renderSettingContent() {
    const { isLoading, orgList, tenant } = this.state;

    const addNewRow = ({ arrayHelpers }) => {
      arrayHelpers.push({
        orgId: null,
        orgIdentifer: '',
        orgName: '',
        product: 'meraki',
        tenantId: tenant,
      });
      
      // Scroll to bottom after adding new row
      setTimeout(() => {
        if (this.mainRowContainerRef.current) {
          this.mainRowContainerRef.current.scrollTop = this.mainRowContainerRef.current.scrollHeight;
        }
      }, 100);
    };

    const deleteAccepted = ({ arrayHelpers, index }) => {
      arrayHelpers.remove(index);
    };

    const deleteButtonOnClicked = (e, { arrayHelpers, item, index }) => {
      const { orgId } = item;
      if (orgId) {
        confirmDialog({
          message: COMMON_TEXT.DELETE_CONFIRM_MESSAGE,
          header: COMMON_TEXT.DELETE_CONFIRMATION_HEADER,
          icon: '',
          accept: () => deleteAccepted({ arrayHelpers, index }),
          reject: () => { },
          acceptClassName: 'p-button-danger',
          rejectClassName: 'p-button-secondary p-button-text',
          appendTo: 'self',
        });
      } else {
        deleteAccepted({ arrayHelpers, index });
      }
    };

    const handleSubmit = (values, { setSubmitting, errors }) => {
      if (!errors) {
        const { newItems, deletedItems, updatedItems } = this.findItemChanges({
          updatedArray: values.orgs,
          originalArray: this.originalOrgList,
        });
        const updatePayLoad = this.getUpdateDataPayload({
          newItems,
          deletedItems,
          updatedItems,
        });
        const { values: payloadValues } = updatePayLoad;
        if (payloadValues.length === 0) {
          return;
        }
        this.updateData({ updatePayLoad });
      }
    };

    const isFormFieldInvalid = ({ name, touched, errors }) => {
      const error = getIn(errors, name);
      const touch = getIn(touched, name);
      return touch && error ? 'p-invalid' : '';
    };

    const getFormErrorMessage = ({ name, touched, errors }) => {
      return isFormFieldInvalid({ name, touched, errors }) ? (
        <span className="validation-error-message">{getIn(errors, name)}</span>
      ) : (
        <></>
      );
    };

    const formSchema = Yup.object().shape({
      orgs: Yup.array()
        .orgIdentiferUnique('orgIdentifer', 'orgIdentifer is duplicated')
        .of(
          Yup.object().shape({
            orgIdentifer: Yup.string().required(COMMON_TEXT.REQUIRED),
            orgName: Yup.string().required(COMMON_TEXT.REQUIRED),
            product: Yup.string().required(COMMON_TEXT.REQUIRED),
          })
        ),
    });

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Formik
            initialValues={{
              orgs: orgList,
            }}
            validationSchema={formSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, handleChange, touched, errors, resetForm }) => (
              <Form id="organization-setting-form">
                <FieldArray
                  name="orgs"
                  render={arrayHelpers => (
                    <>
                      <div className="main-row-container" ref={this.mainRowContainerRef}>
                        <div className="right-content">
                          {values.orgs.map((org, index) => (
                            <div className="row-container" key={index}>
                              <div className="key-field">
                                <div className="input-text-field-container">
                                  <span className="input-top-title">ID</span>
                                  <InputText
                                    id={`orgs[${index}].orgIdentifer`}
                                    value={org.orgIdentifer}
                                    onChange={handleChange}
                                    className={`${isFormFieldInvalid({
                                      name: `orgs[${index}].orgIdentifer`,
                                      touched,
                                      errors,
                                    })}`}
                                  />
                                  {getFormErrorMessage({
                                    name: `orgs[${index}].orgIdentifer`,
                                    touched,
                                    errors,
                                  })}
                                </div>
                                <div className="input-text-field-container">
                                  <span className="input-top-title">名前</span>
                                  <InputText
                                    id={`orgs[${index}].orgName`}
                                    value={org.orgName}
                                    onChange={handleChange}
                                    className={`${isFormFieldInvalid({
                                      name: `orgs[${index}].orgName`,
                                      touched,
                                      errors,
                                    })}`}
                                  />
                                  {getFormErrorMessage({
                                    name: `orgs[${index}].orgName`,
                                    touched,
                                    errors,
                                  })}
                                </div>
                                <div className="input-text-field-container">
                                  <span className="input-top-title">プロダクト</span>
                                  <div className="figma-dropdown">
                                    <Dropdown
                                      id={`orgs[${index}].product`}
                                      value={org.product}
                                      options={this.orgProductTypeOptions}
                                      onChange={handleChange}
                                      className={`${isFormFieldInvalid({
                                        name: `orgs[${index}].product`,
                                        touched,
                                        errors,
                                      })}`}
                                    />
                                  </div>
                                  {getFormErrorMessage({
                                    name: `orgs[${index}].product`,
                                    touched,
                                    errors,
                                  })}
                                </div>
                                <div className="input-text-field-container">
                                  <Button
                                    type="button"
                                    text
                                    severity="danger"
                                    className="icon-only-delete-button"
                                    onClick={e =>
                                      deleteButtonOnClicked(e, {
                                        arrayHelpers,
                                        item: org,
                                        index,
                                      })
                                    }
                                  >
                                    <span className="delete-icon">
                                      <img src="assets/img/trash-icon.svg" alt="Delete" />
                                    </span>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button
                        severity="secondary"
                        type="button"
                        label="追加"
                        className="add-button has-shadow"
                        onClick={() => addNewRow({ arrayHelpers })}
                      />
                    </>
                  )}
                />
              </Form>
            )}
          </Formik>
        )}
      </>
    );
  }

  render() {
    return (
      <>
        <ConfirmDialog />
        <div className="config-container custom-config-container organization-setting-container custom-setting-config-container">
          <div className="config-title-container">
            <div className="text-left">
              <div className="title-text">
                {COMMON_TEXT.ORGANIZATION_SETTING}
              </div>
            </div>
            <div className="grid grid-nogutter align-items-center justify-content-end">
              <SaveButton
                type="submit"
                loading={this.state.isUpdating}
                form="organization-setting-form"
                label="保存"
                iconSrc="assets/img/save-setting-icon.svg"
                iconAlt="Save"
              />
            </div>
          </div>
          <div className="config-content">
            <div className="setting-content">{this.renderSettingContent()}</div>
          </div>
        </div>
      </>
    );
  }
}

OrganizationSetting.propTypes = {};

export default OrganizationSetting;
